/**
 * Disguise — wearing the building's own uniforms to walk its corridors.
 *
 * The other stealth tools hide you: darkness, cover, a cabinet. A uniform does
 * the opposite. You stay in plain sight and the question changes from "can they
 * see me" to "do I look like I belong here". That question is what this module
 * answers, as a single 0..1 EXPOSURE factor folded into the awareness pass:
 *
 *   0 = reads as staff, nobody looks twice
 *   1 = made — the uniform is doing nothing
 *
 * Three things give you away, per the design:
 *   - AREA. A worker in the open-plan office is invisible; the same worker in
 *     the server room, the security office or a basement plant room is not.
 *     Each uniform carries the access its wearer would really have.
 *   - BEHAVIOUR. Staff walk. Sprinting, creeping about crouched, or carrying a
 *     drawn weapon reads wrong whatever you're wearing.
 *   - PROXIMITY. Up close, a colleague sees a face they don't know. Inside
 *     `madeRadius` the disguise fails outright, so a uniform buys you distance,
 *     never immunity.
 *
 * Pure + headless (rule 4): scoring only. Who wears what, and what they do
 * about it, belongs to the caller.
 */

import { CONFIG } from '../config';
import { roomAt } from './light';
import type { MapData } from './map';
import type { Player } from './player';

/** The uniforms in the building. */
export type DisguiseKind = 'guard' | 'janitor' | 'worker';

export const DISGUISE_LABEL: Record<DisguiseKind, string> = {
  guard: 'Security uniform',
  janitor: 'Janitor coveralls',
  worker: 'Office clothes',
};

/** Is a drawn weapon in the player's hands right now? A knife reads as nothing
 *  much; a rifle across your chest reads as a great deal. */
function weaponDrawn(player: Player): boolean {
  if (player.sheathed) return false; // hands empty — nothing to see
  const w = player.weapons[player.activeWeaponIdx];
  return w !== undefined && !w.isMelee;
}

/**
 * How exposed the player is to `observerX/Y` right now, 0..1. Undisguised is
 * always 1 — this only ever helps someone wearing something.
 *
 * The result multiplies the normal detection factor in `awareness.ts`, so a
 * good disguise doesn't make you invisible: it slows the meter to a crawl and
 * buys the seconds you need to walk out of the room.
 */
export function exposure(
  player: Player,
  map: MapData,
  observerX: number,
  observerY: number,
): number {
  const kind = player.disguise;
  if (kind === null) return 1;
  const cfg = CONFIG.disguise;
  const rules = cfg.kinds[kind];

  // Close enough to see your face — the uniform stops mattering.
  const cheb = Math.max(Math.abs(player.x - observerX), Math.abs(player.y - observerY));
  if (cheb <= cfg.madeRadius) return 1;

  let e = rules.base;
  // AREA: somewhere this uniform has no business being.
  const room = roomAt(map, player.x, player.y);
  if (room?.kind !== undefined && (rules.restricted as readonly string[]).includes(room.kind)) {
    e += cfg.restrictedPenalty;
  }
  // BEHAVIOUR: staff walk, empty-handed.
  if (player.stance === 'run') e += cfg.runPenalty;
  else if (player.stance === 'crouch') e += cfg.crouchPenalty;
  if (weaponDrawn(player)) e += cfg.weaponPenalty;
  if (player.inCover) e += cfg.coverPenalty; // nobody hugs a wall on their way to a meeting

  return Math.max(0, Math.min(1, e));
}

/** Would this uniform be out of place in `roomKind`? Used for UI warnings. */
export function isRestrictedFor(kind: DisguiseKind, roomKind: string | undefined): boolean {
  if (roomKind === undefined) return false;
  return (CONFIG.disguise.kinds[kind].restricted as readonly string[]).includes(roomKind);
}
